import { extendTheme, theme as baseChakraTheme } from '@chakra-ui/react';
import fontSizes from './foundations/fontSizes';
import lineHeights from './foundations/lineHeights';
import styles from './foundations/styles';
import sizes from './foundations/sizes';
import spacing from './foundations/spacing';
import textStyles from './foundations/textStyles';
import colors from './foundations/colors';
import baseColors, { Swatch } from './foundations/baseColors';
import shadows from './foundations/shadows';
import breakpoints from './foundations/breakpoints';
import radii from './foundations/radii';
import components from './components';
import { ThemeName } from './themes';
import { generateThemeColors } from './utils/themegenerator';

const extendedTheme = extendTheme({
  config: {
    initialColorMode: 'light',
    useSystemColorMode: false,
  },
  colors: baseColors,
  space: spacing,
  sizes,
  lineHeights,
  fontSizes,
  textStyles,
  shadows,
  styles,
  radii,
  zIndices: {
    ...baseChakraTheme.zIndices,
    // Extend Chakra's default zIndices with our custom high dropdown value
    dropdownHigh: 9999999, // Higher than SessionRecordingUrOverlay (999999)
  },
  components,
  fonts: {
    heading: `Circular, sans-serif`,
    body: `Circular, sans-serif`,
  },
});

// We explicitly override the breakpoints here because the combination of
// our own breakpoints and the built-in ones causes clashes
const baseTheme = { ...extendedTheme, breakpoints } as {
  colors: typeof baseColors;
  breakpoints: typeof breakpoints;
  space: typeof spacing;
  sizes: typeof sizes;
  lineHeights: typeof lineHeights;
  fontSizes: typeof fontSizes;
  textStyles: typeof textStyles;
  shadows: typeof shadows;
  styles: typeof styles;
  radii: typeof radii;
};

export type CustomTheme = typeof extendedTheme &
  typeof baseTheme & { colors: Swatch };

export const getTheme = (
  themeName: ThemeName | undefined,
  fallback?: {
    primary_color?: string;
    secondary_color?: string;
  },
): CustomTheme => {
  const themeColors =
    colors[themeName || ''] ||
    generateThemeColors(
      fallback?.primary_color || '#FF8000',
      fallback?.secondary_color || '#6F558A',
    );

  // Override after theme generation
  // If these were set in the theme object, it would crash as the colors are not logical, sequential or contain duplicates
  if (themeName === 'yellowhive') {
    themeColors.secondary[800] = '#FFFFFF'; // sidebar background
    themeColors.secondary[600] = '#FFFFFF'; // sidebar lang switch background
    themeColors.secondary[500] = '#FFFFFF'; // sidebar lang switch background: hover
    themeColors.secondary[300] = '#1B3158'; // sidebar text
    themeColors.secondary[50] = '#1B3158'; // sidebar lang switch text
  }

  return {
    ...baseTheme,
    colors: {
      ...baseTheme.colors,
      ...themeColors,
    },
  };
};

export default baseTheme;
